/**
 * Spreadsheet (.xlsx) build/parse for the admin product & retailer-price
 * import/export tools.
 *
 * Why .xlsx and not CSV: the catalog carries GS1 codes (GTIN/UPC/EAN) that are
 * digit strings with leading zeros and up to 14 digits. Excel silently mangles
 * those in CSV — stripping leading zeros and rewriting long codes as scientific
 * notation — the moment the file is opened, before any edit. A real workbook
 * with each cell explicitly typed (codes as TEXT, prices as NUMBER) round-trips
 * through Excel untouched: no import wizard, no re-typing, no quote noise.
 *
 * The column spec is the single source of truth for header text and field
 * mapping. Export writes headers + typed cells; import matches the header row
 * back to field keys (header-driven — only columns present are returned).
 */

import * as XLSX from 'xlsx';

export type SheetCellType = 'text' | 'number' | 'code' | 'bool';

export interface SheetColumn {
  /** Field key exchanged with the API. */
  key: string;
  /** Human header written to / read from the sheet. */
  header: string;
  type: SheetCellType;
  /** Reference-only column the importer never writes (shown for context). */
  locked?: boolean;
}

export interface ParsedSheet {
  /** Field keys whose header was found in the sheet (header-driven set). */
  columns: string[];
  /** One object per data row, keyed by field. */
  rows: Record<string, unknown>[];
  /** Header cells that matched no known column (surfaced as a warning). */
  unknownHeaders: string[];
}

/** Master catalog export/import columns (products + MSRP pricing). */
export const MASTER_COLUMNS: SheetColumn[] = [
  { key: 'id', header: 'ID', type: 'number', locked: true },
  { key: 'productId', header: 'Product ID', type: 'text' },
  { key: 'sku', header: 'SKU', type: 'text' },
  { key: 'name', header: 'Name', type: 'text' },
  { key: 'acronym', header: 'Type', type: 'text' },
  { key: 'category', header: 'Category', type: 'text' },
  { key: 'color', header: 'Color', type: 'text' },
  { key: 'lengthFeet', header: 'Length (ft)', type: 'number' },
  { key: 'sqft', header: 'Sqft', type: 'number' },
  { key: 'description', header: 'Description', type: 'text' },
  { key: 'gs1Prefix', header: 'GS1 Prefix', type: 'code' },
  { key: 'gtin', header: 'GTIN', type: 'code' },
  { key: 'gtin8', header: 'GTIN-8', type: 'code' },
  { key: 'upc', header: 'UPC', type: 'code' },
  { key: 'ean', header: 'EAN', type: 'code' },
  { key: 'isActive', header: 'Active', type: 'bool' },
  { key: 'msrpUsd', header: 'MSRP USD', type: 'number' },
  { key: 'msrpCad', header: 'MSRP CAD', type: 'number' },
];

/** Per-retailer price export/import columns (price-only import). */
export const RETAILER_COLUMNS: SheetColumn[] = [
  { key: 'id', header: 'ID', type: 'number', locked: true },
  { key: 'sku', header: 'SKU', type: 'text', locked: true },
  { key: 'name', header: 'Name', type: 'text', locked: true },
  { key: 'acronym', header: 'Type', type: 'text', locked: true },
  { key: 'color', header: 'Color', type: 'text', locked: true },
  { key: 'lengthFeet', header: 'Length (ft)', type: 'number', locked: true },
  { key: 'priceUsd', header: 'Price USD', type: 'number' },
  { key: 'priceCad', header: 'Price CAD', type: 'number' },
];

/** Labels used to render field diffs in the preview UI. */
export function columnLabel(columns: SheetColumn[], key: string): string {
  return columns.find(c => c.key === key)?.header ?? key;
}

const normalizeHeader = (s: string): string => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');

/** Cell value for export, typed by column so Excel stores it faithfully. */
function toCell(col: SheetColumn, raw: unknown): XLSX.CellObject | string | number {
  if (col.type === 'bool') {
    return { t: 's', v: raw ? 'Yes' : 'No' };
  }
  if (col.type === 'code') {
    // Force TEXT so leading zeros / 14-digit codes survive, and so Excel keeps
    // treating the cell as text when the user edits it.
    const s = raw === null || raw === undefined ? '' : String(raw);
    return { t: 's', v: s, z: '@' };
  }
  if (col.type === 'number') {
    if (raw === null || raw === undefined || raw === '') return { t: 's', v: '' };
    return { t: 'n', v: Number(raw) };
  }
  // text
  const s = raw === null || raw === undefined ? '' : String(raw);
  return { t: 's', v: s };
}

/**
 * Build an .xlsx workbook from typed rows and trigger a browser download.
 * `rows` are objects keyed by column.key.
 */
export function downloadWorkbook(
  columns: SheetColumn[],
  rows: Record<string, unknown>[],
  filename: string,
  sheetName = 'Products',
): void {
  const headerRow = columns.map(c => c.header);
  const aoa: (XLSX.CellObject | string | number)[][] = [headerRow];
  for (const row of rows) {
    aoa.push(columns.map(c => toCell(c, row[c.key])));
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa as unknown[][]);

  // Column widths + an explicit text format on code columns (belt and braces
  // alongside the per-cell z='@').
  ws['!cols'] = columns.map(c => {
    if (c.type === 'code') return { wch: 18 };
    if (c.key === 'name' || c.key === 'description') return { wch: 34 };
    if (c.type === 'number') return { wch: 12 };
    return { wch: 16 };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

/** Coerce a parsed cell to the JSON value the API expects for this column. */
function fromCell(col: SheetColumn, raw: unknown): unknown {
  const blank = raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '');
  if (col.type === 'code' || col.type === 'text') {
    if (blank) return null;
    return typeof raw === 'string' ? raw.trim() : String(raw);
  }
  if (col.type === 'number') {
    if (blank) return null;
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : String(raw); // pass through junk so the API reports it
  }
  // bool — leave the raw text/boolean for the API to interpret (blank = leave unchanged)
  if (blank) return null;
  return raw;
}

/**
 * Parse an uploaded .xlsx/.xls file into API-ready rows. Header-driven: only
 * columns whose header matches the spec are returned in `columns`/`rows`.
 */
export async function parseWorkbook(file: File, columns: SheetColumn[]): Promise<ParsedSheet> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('The file has no sheets');
  const ws = wb.Sheets[sheetName];

  const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, blankrows: false, defval: null });
  if (matrix.length === 0) throw new Error('The sheet is empty');

  const headerCells = (matrix[0] as unknown[]).map(h => (h === null || h === undefined ? '' : String(h)));

  // Map each header column index → spec column, by normalized header or key.
  const lookup = new Map<string, SheetColumn>();
  for (const col of columns) {
    lookup.set(normalizeHeader(col.header), col);
    lookup.set(normalizeHeader(col.key), col);
  }

  const indexToCol: (SheetColumn | null)[] = [];
  const unknownHeaders: string[] = [];
  const present: SheetColumn[] = [];
  headerCells.forEach((h, idx) => {
    const norm = normalizeHeader(h);
    const col = norm ? lookup.get(norm) ?? null : null;
    indexToCol[idx] = col;
    if (!col && h.trim() !== '') unknownHeaders.push(h);
    if (col && !present.includes(col)) present.push(col);
  });

  const rows: Record<string, unknown>[] = [];
  for (let r = 1; r < matrix.length; r++) {
    const cells = (matrix[r] as unknown[]) ?? [];
    // Skip fully-empty rows.
    if (cells.every(c => c === null || c === undefined || (typeof c === 'string' && c.trim() === ''))) continue;
    const obj: Record<string, unknown> = {};
    indexToCol.forEach((col, idx) => {
      if (!col) return;
      obj[col.key] = fromCell(col, cells[idx]);
    });
    rows.push(obj);
  }

  return { columns: present.map(c => c.key), rows, unknownHeaders };
}
